import React, { useState, useEffect, useCallback } from "react";
import {
  Shield, Globe, Activity, Ban, AlertTriangle, RefreshCw, Plus, Trash2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WAFMonitorPage() {
  const [status, setStatus] = useState(null);
  const [blockedIps, setBlockedIps] = useState([]);
  const [events, setEvents] = useState([]);
  const [newIp, setNewIp] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const opts = { credentials: "include" };
    const [s, b, e] = await Promise.all([
      fetch(`${API}/api/waf/status`, opts).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/waf/blocked-ips`, opts).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/waf/suspicious-activity?limit=30`, opts).then(r => r.ok ? r.json() : null),
    ]);
    setStatus(s);
    setBlockedIps(b?.blocked_ips || []);
    setEvents(e?.events || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const blockIp = async () => {
    if (!newIp) return;
    await fetch(`${API}/api/waf/block-ip`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ip: newIp }),
    });
    setNewIp("");
    fetchAll();
  };

  const unblockIp = async (ip) => {
    await fetch(`${API}/api/waf/unblock-ip`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ip }),
    });
    fetchAll();
  };

  return (
    <div className="min-h-screen bg-[#0D0F14] text-white p-6 lg:p-10" data-testid="waf-monitor-page">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-3">
        <Shield size={24} className="text-[#C9A84C]" /> WAF & DDoS Protection
      </h1>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="rounded-xl p-4 bg-[rgba(74,222,128,0.05)] border border-[rgba(74,222,128,0.2)]">
          <Globe size={16} className="text-[#4ADE80] mb-2" />
          <div className="text-2xl font-bold">{status?.enabled ? "ACTIVE" : "—"}</div>
          <div className="text-xs text-[#8B95A5]">WAF Status</div>
        </div>
        <div className="rounded-xl p-4 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
          <Activity size={16} className="text-[#36A9E1] mb-2" />
          <div className="text-2xl font-bold">{status?.global_rate_limit || "—"}</div>
          <div className="text-xs text-[#8B95A5]">Rate Limit</div>
        </div>
        <div className="rounded-xl p-4 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
          <Ban size={16} className="text-[#F87171] mb-2" />
          <div className="text-2xl font-bold">{blockedIps.length}</div>
          <div className="text-xs text-[#8B95A5]">Blocked IPs</div>
        </div>
        <div className="rounded-xl p-4 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
          <AlertTriangle size={16} className="text-[#FBBF24] mb-2" />
          <div className="text-2xl font-bold">{events.length}</div>
          <div className="text-xs text-[#8B95A5]">Suspicious Events</div>
        </div>
      </div>

      {/* IP Block Management */}
      <div className="rounded-xl border border-[rgba(255,255,255,0.06)] p-4 mb-6">
        <h3 className="text-sm font-semibold text-white mb-3">IP Blocklist</h3>
        <div className="flex gap-2 mb-3">
          <input value={newIp} onChange={e => setNewIp(e.target.value)} placeholder="Enter IP address"
            className="flex-1 bg-[#0D0F14] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm text-white"
            data-testid="block-ip-input" />
          <button onClick={blockIp} className="px-4 py-2 bg-[#F87171] text-white rounded text-sm font-semibold flex items-center gap-1"
            data-testid="block-ip-btn"><Plus size={14} /> Block</button>
        </div>
        {blockedIps.length > 0 ? (
          <div className="space-y-1">
            {blockedIps.map(ip => (
              <div key={ip} className="flex items-center justify-between py-1.5 px-3 bg-[rgba(248,113,113,0.05)] rounded text-sm">
                <span className="font-mono text-[#F87171]">{ip}</span>
                <button onClick={() => unblockIp(ip)} className="text-[#8B95A5] hover:text-white"
                  data-testid={`unblock-${ip}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-[#555]">No blocked IPs</p>}
      </div>

      {/* Suspicious Activity Log */}
      <div className="rounded-xl border border-[rgba(255,255,255,0.06)] overflow-hidden">
        <div className="bg-[rgba(255,255,255,0.03)] px-4 py-2 flex items-center justify-between">
          <span className="text-xs text-[#8B95A5] uppercase tracking-wider">Suspicious Activity Log</span>
          <button onClick={fetchAll} className="text-[#8B95A5] hover:text-white"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
        </div>
        {events.length > 0 ? (
          <table className="w-full" data-testid="suspicious-activity-table">
            <thead><tr className="bg-[rgba(255,255,255,0.02)]">
              <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Time</th>
              <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">IP</th>
              <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Reason</th>
              <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Path</th>
            </tr></thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={i} className="border-b border-[rgba(255,255,255,0.04)]">
                  <td className="py-2 px-3 text-[11px] text-[#8B95A5]">{new Date(e.timestamp).toLocaleString()}</td>
                  <td className="py-2 px-3 text-[11px] font-mono text-white">{e.ip}</td>
                  <td className="py-2 px-3 text-[11px] text-[#FBBF24]">{e.reason}</td>
                  <td className="py-2 px-3 text-[11px] text-[#8B95A5] truncate max-w-[200px]">{e.path}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-center py-8 text-[#555] text-sm" data-testid="no-suspicious-events">No suspicious activity detected</p>}
      </div>
    </div>
  );
}
